// Reading an HTML plan (today's plans/parse.ts, ported).
//
// `parsePlanHtml` reads the body only from its sanitised fragment
// (sanitize.ts), never from a raw parse, and walks it with a loop rather than
// by recursion, so a body nested deep costs a counter, not the stack. A body
// past a bound, or whose top level is off the plan's structure, comes back as
// `fallback` with `diagnostics` saying why; an oddity it can still draw
// around is one of `notes`. What it keeps of the body are the sanitiser's own
// nodes (model.ts, `Content`): nothing is serialised to be parsed again.
//
// `planKeys` is the listing grammar on the raw body, for the mock and its
// tests: a template's content is inert, so it runs nothing and loads nothing.
// The server reads the same grammar (crates/poulpe-server/src/plans), and
// both pass the cases of its `cases.json`.
import { keysIn, type KeyMatch } from '../lib/keys'
import type { Content, ExitCondition, PlanDecision, PlanItem, PlanLane, PlanModel, PlanPhase, PlanWait } from './model'
import { sanitizePlanFragment } from './sanitize'

/** Bounds on what the page reads: the tests pass small ones, to test each edge cheaply. */
export interface PlanBounds { readonly phases: number; readonly items: number; readonly depth: number; readonly elements: number }

/** What the page reads at most. */
export const PLAN_BOUNDS: PlanBounds = { phases: 64, items: 2_000, depth: 16, elements: 20_000 }

const count = (n: number) => n.toLocaleString('en-GB')

const ELEMENT_NODE = 1
const TEXT_NODE = 3

/** Elements whose text is neither a listing nor a mention. */
const UNREAD = new Set(['code', 'pre', 'script', 'style', 'template', 'noscript'])
/** Elements whose text runs on with what is around them; any other element breaks a text in two. */
const INLINE = new Set([
  'a', 'abbr', 'b', 'bdi', 'bdo', 'big', 'cite', 'data', 'del', 'dfn', 'em', 'font', 'i', 'img', 'ins', 'kbd',
  'label', 'mark', 'nobr', 'q', 's', 'samp', 'small', 'span', 'strike', 'strong', 'sub', 'sup', 'time', 'tt', 'u',
  'var', 'wbr',
])

// ---------------------------------------------------------------- walking

export interface Visitor {
  /** An element, at its depth below the root (its children are at 1). False skips what it holds; 'stop' ends the walk. */
  enter?(element: Element, depth: number): boolean | 'stop' | void
  /** Once for every element entered, skipped or not, after what it holds. */
  leave?(element: Element): void
  /** A text node; 'stop' ends the walk. */
  text?(node: Text): 'stop' | void
}

/** Every node under `root`, in document order, with a loop: no recursion on the tree's depth. */
export function walk(root: Node, visit: Visitor): void {
  let node: Node | null = root.firstChild
  let depth = 1
  while (node) {
    if (node.nodeType === ELEMENT_NODE) {
      const into = visit.enter?.(node as Element, depth)
      if (into === 'stop') return
      if (into !== false && node.firstChild) {
        node = node.firstChild
        depth++
        continue
      }
      visit.leave?.(node as Element)
    } else if (node.nodeType === TEXT_NODE) {
      if (visit.text?.(node as Text) === 'stop') return
    }
    while (!node.nextSibling) {
      node = node.parentNode
      depth--
      if (!node || node === root) return
      visit.leave?.(node as Element)
    }
    node = node.nextSibling
  }
}

/** The text keys are read in: decoded, without what is unread, and broken where a block begins or ends. */
export function readable(root: Node, unread: ReadonlySet<string> = UNREAD): string {
  let text = ''
  walk(root, {
    enter(element) {
      if (!INLINE.has(element.localName)) text += '\n'
      return !unread.has(element.localName)
    },
    leave(element) {
      if (!INLINE.has(element.localName)) text += '\n'
    },
    text(node) {
      text += node.data
    },
  })
  return text
}

const LEADING = /\S/

/** A raw body parsed where nothing runs or loads: a template's content, never a document. For the listing grammar alone. */
export function inert(body: string): DocumentFragment {
  const template = document.createElement('template')
  template.innerHTML = body
  return template.content
}

/** Enough of an item's first words to hold a key, read no further: nested items never read each other's whole text. */
const LEADING_CHARS = 64

function leadingText(item: Element): string {
  let text = ''
  walk(item, {
    enter(element) {
      if (!INLINE.has(element.localName)) text += '\n'
      return text.trim().length >= LEADING_CHARS ? 'stop' : !UNREAD.has(element.localName)
    },
    text(node) {
      text += node.data
      return text.trim().length >= LEADING_CHARS ? 'stop' : undefined
    },
  })
  return text
}

/** The key an item's first words begin with, as `keysIn` finds it there, or null. */
export function leadingKey(item: Element, names: readonly string[]): KeyMatch | null {
  const text = leadingText(item)
  const start = text.search(LEADING)
  if (start < 0) return null
  const found = keysIn(text.slice(start), names)[0]
  return found && found.index === 0 ? found : null
}

function keysOf(text: string, names: readonly string[]): string[] {
  return [...new Set(keysIn(text, names).map((found) => found.key))]
}

export function hasClass(element: Element, name: string): boolean {
  return element.classList.contains(name)
}

export function isA(node: Node, tag: string, name?: string): node is Element {
  return node.nodeType === ELEMENT_NODE && (node as Element).localName === tag && (name === undefined || hasClass(node as Element, name))
}

// ---------------------------------------------------------------- the listing grammar, on the raw body

/**
 * Which issues an HTML plan lists, and which it only mentions: an `li`
 * inside an element of class `pp-issues` lists the key its first words begin
 * with; any other key in the text is a mention. Comments, attributes and the
 * text of code, pre, script, style, template and noscript are neither.
 */
export function planKeys(body: string, names: readonly string[]): { listed: string[]; mentioned: string[] } {
  const doc = inert(body)
  const listed = new Set<string>()
  let all = ''
  let inside = 0
  walk(doc, {
    enter(element) {
      const name = element.localName
      if (!INLINE.has(name)) all += '\n'
      if (UNREAD.has(name)) return false
      if (inside > 0 && name === 'li') {
        const found = leadingKey(element, names)
        if (found) listed.add(found.key)
      }
      if (hasClass(element, 'pp-issues')) inside++
      return true
    },
    leave(element) {
      if (!INLINE.has(element.localName)) all += '\n'
      if (!UNREAD.has(element.localName) && hasClass(element, 'pp-issues')) inside--
    },
    text(node) {
      all += node.data
    },
  })
  return { listed: [...listed], mentioned: keysOf(all, names).filter((key) => !listed.has(key)) }
}

/** The issues an HTML plan lists, in the order it lists them. */
export function listedKeys(body: string, names: readonly string[]): string[] {
  return planKeys(body, names).listed
}

// ---------------------------------------------------------------- the plan's model

type TopKind = 'objective' | 'phases' | 'decisions' | 'annex'
const TOP: readonly [TopKind, string, string][] = [
  ['objective', 'p', 'pp-objective'],
  ['phases', 'section', 'pp-phases'],
  ['decisions', 'section', 'pp-decisions'],
  ['annex', 'section', 'pp-annex'],
]

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/
const EXCERPT = 60

/** An element as the structure names one: `section.pp-phase`, `ul`. */
function selector(element: Element): string {
  return [element.localName, ...element.classList].join('.')
}

function words(element: Element): string {
  return (element.textContent ?? '').replace(/\s+/g, ' ').trim()
}

function excerpt(element: Element): string {
  const text = words(element)
  return text.length > EXCERPT ? `${text.slice(0, EXCERPT - 1).trimEnd()}…` : text
}

/** A real day, `2026-10-08`; null for anything else. */
function day(value: string | null): string | null {
  const found = value === null ? null : DAY.exec(value.trim())
  if (!found) return null
  const [year, month, date] = [Number(found[1]), Number(found[2]), Number(found[3])]
  const at = new Date(Date.UTC(year, month - 1, date))
  return at.getUTCFullYear() === year && at.getUTCMonth() === month - 1 && at.getUTCDate() === date ? found[0] : null
}

function slug(title: string): string {
  const plain = title.normalize('NFKD').replace(/\p{M}+/gu, '').toLowerCase()
  const kept = plain.replace(/[^a-z0-9]+/g, '-').replace(/^-+/, '').slice(0, 60).replace(/-+$/, '')
  return kept ? `phase-${kept}` : 'phase'
}

/** What an element holds, trimmed of the white space at its ends, as the nodes themselves. */
function contentOf(element: Element): Content {
  const nodes = [...element.childNodes]
  while (nodes.length && nodes[0]!.nodeType === TEXT_NODE && !(nodes[0] as Text).data.trim()) nodes.shift()
  while (nodes.length && nodes.at(-1)!.nodeType === TEXT_NODE && !(nodes.at(-1) as Text).data.trim()) nodes.pop()
  const first = nodes[0]
  if (first?.nodeType === TEXT_NODE) nodes[0] = first.ownerDocument!.createTextNode((first as Text).data.trimStart())
  const last = nodes.at(-1)
  if (last?.nodeType === TEXT_NODE) nodes[nodes.length - 1] = last.ownerDocument!.createTextNode((last as Text).data.trimEnd())
  return nodes
}

/** Whether the body is past a bound; it stops at the first. */
function pastBound(fragment: DocumentFragment, bounds: PlanBounds): string | null {
  let elements = 0
  let phases = 0
  let items = 0
  let inside = 0
  let past: string | null = null
  walk(fragment, {
    enter(element, depth) {
      elements++
      if (depth > bounds.depth) past = `The plan is nested more than ${count(bounds.depth)} levels deep.`
      else if (elements > bounds.elements) past = `The plan has more than ${count(bounds.elements)} elements.`
      else if (isA(element, 'section', 'pp-phase') && ++phases > bounds.phases) past = `The plan has more than ${count(bounds.phases)} phases.`
      else if (inside > 0 && element.localName === 'li' && ++items > bounds.items) past = `The plan has more than ${count(bounds.items)} issue lines.`
      if (past) return 'stop'
      if (hasClass(element, 'pp-issues')) inside++
      return true
    },
    leave(element) {
      if (hasClass(element, 'pp-issues')) inside--
    },
  })
  return past
}

class PlanReader {
  readonly notes: string[] = []
  private readonly ids = new Set<string>()

  constructor(private readonly names: readonly string[]) {}

  /** The `h2` and the phases of `section.pp-phases`. */
  phasesSection(section: Element): { title: string; phases: PlanPhase[] } {
    const read: { title: string; phases: PlanPhase[] } = { title: '', phases: [] }
    let titled = false
    for (const child of section.childNodes) {
      if (isA(child, 'h2') && !titled && read.phases.length === 0) {
        read.title = words(child)
        titled = true
      } else if (isA(child, 'section', 'pp-phase')) {
        read.phases.push(this.phase(child))
      } else {
        this.skipped(child, selector(section))
      }
    }
    if (!titled) this.notes.push(`${selector(section)} has no h2.`)
    return read
  }

  phase(section: Element): PlanPhase {
    const heading = [...section.children].find((child) => child.localName === 'h3') ?? null
    const title = heading ? words(heading) : ''
    const where = title ? `“${title}”` : 'a phase with no h3'
    const phase: PlanPhase = {
      id: this.id(slug(title)),
      title,
      parked: section.getAttribute('data-state')?.trim().toLowerCase() === 'parked',
      goal: null,
      exit: [],
      waits: [],
      lanes: [],
    }
    let hasExit = false
    let lane: Element | null = null
    const laneWithoutList = (title: Element) => this.notes.push(`${where}: the lane “${words(title)}” has no issues list.`)
    for (const child of section.childNodes) {
      if (child === heading) continue
      if (isA(child, 'p', 'pp-goal') && phase.goal === null) {
        phase.goal = contentOf(child)
      } else if (isA(child, 'ul', 'pp-exit') && !hasExit) {
        hasExit = true
        phase.exit = this.exit(child, where)
      } else if (isA(child, 'p', 'pp-waits')) {
        phase.waits.push(this.wait(child))
      } else if (isA(child, 'p', 'pp-lane')) {
        if (lane) laneWithoutList(lane)
        lane = child
      } else if (isA(child, 'ul', 'pp-issues')) {
        phase.lanes.push(this.lane(child, lane, where))
        lane = null
      } else {
        this.skipped(child, where)
      }
    }
    if (lane) laneWithoutList(lane)
    return phase
  }

  decisions(section: Element): PlanDecision[] {
    const decisions: PlanDecision[] = []
    let titled = false
    for (const child of section.childNodes) {
      if (isA(child, 'h2') && !titled && decisions.length === 0) {
        titled = true
      } else if (isA(child, 'ul')) {
        for (const item of child.childNodes) {
          if (isA(item, 'li')) decisions.push(this.decision(item))
          else this.skipped(item, selector(section))
        }
      } else {
        this.skipped(child, selector(section))
      }
    }
    return decisions
  }

  annex(section: Element): Content | null {
    const kept: Node[] = []
    let titled = false
    for (const child of section.childNodes) {
      if (isA(child, 'h2') && !titled && kept.length === 0) titled = true
      else if (isA(child, 'details')) kept.push(child)
      else this.skipped(child, selector(section))
    }
    return kept.length ? kept : null
  }

  private exit(list: Element, where: string): ExitCondition[] {
    const conditions: ExitCondition[] = []
    for (const child of list.childNodes) {
      if (!isA(child, 'li')) {
        this.skipped(child, where)
        continue
      }
      const said = child.getAttribute('data-met')
      const metOn = day(said)
      if (said !== null && metOn === null) this.notes.push(`${where}: data-met="${said}" is not a day.`)
      conditions.push({ content: contentOf(child), keys: keysOf(readable(child), this.names), metOn })
    }
    return conditions
  }

  private wait(paragraph: Element): PlanWait {
    return {
      content: contentOf(paragraph),
      keys: keysOf(readable(paragraph), this.names),
      owner: paragraph.getAttribute('data-on')?.trim().toLowerCase() === 'owner',
    }
  }

  private lane(list: Element, title: Element | null, where: string): PlanLane {
    const items: PlanItem[] = []
    for (const child of list.childNodes) {
      if (!isA(child, 'li')) {
        this.skipped(child, where)
        continue
      }
      const item = this.item(child, where)
      if (item) items.push(item)
    }
    return { title: title ? words(title) : null, items }
  }

  private item(li: Element, where: string): PlanItem | null {
    const found = leadingKey(li, this.names)
    if (!found) {
      this.notes.push(`An item of ${where} begins with no key: “${excerpt(li)}”.`)
      return null
    }
    const copy = li.cloneNode(true) as Element
    const note = [...copy.children].find((child) => child.localName === 'span' && hasClass(child, 'pp-note')) ?? null
    const noteContent = note ? contentOf(note) : null
    note?.remove()
    dropLeading(copy, found.length)
    if (copy.querySelector('li')) this.notes.push(`An item of ${where} holds a list: its items are not drawn as issues.`)
    return { key: found.key, title: contentOf(copy), note: noteContent }
  }

  private decision(li: Element): PlanDecision {
    const copy = li.cloneNode(true) as Element
    const note = [...copy.children].find((child) => child.localName === 'span' && hasClass(child, 'pp-note')) ?? null
    const said = note ? words(note) : null
    note?.remove()
    return { content: contentOf(copy), note: said || null }
  }

  /** A unique anchor: the first phase keeps its slug, a later one with the same takes a number. */
  private id(base: string): string {
    let id = base
    for (let count = 2; this.ids.has(id); count++) id = `${base}-${count}`
    this.ids.add(id)
    return id
  }

  private skipped(node: Node, where: string): void {
    if (node.nodeType === TEXT_NODE) {
      if ((node as Text).data.trim()) this.notes.push(`Text in ${where} is skipped: “${excerptOf((node as Text).data)}”.`)
    } else if (node.nodeType === ELEMENT_NODE) {
      this.notes.push(`${selector(node as Element)} in ${where} is skipped.`)
    }
  }
}

function excerptOf(text: string): string {
  const said = text.replace(/\s+/g, ' ').trim()
  return said.length > EXCERPT ? `${said.slice(0, EXCERPT - 1).trimEnd()}…` : said
}

/** Takes the item's first `length` characters of words (its key) out of it, and what that leaves empty. */
function dropLeading(item: Element, length: number): void {
  let left = length
  let started = false
  const cut = new Set<Element>()
  walk(item, {
    enter(element) {
      if (left === 0) return 'stop'
      return !UNREAD.has(element.localName)
    },
    text(node) {
      if (left === 0) return
      let data = node.data
      if (!started) {
        const start = data.search(LEADING)
        if (start < 0) return
        data = data.slice(start)
        started = true
      }
      const taken = Math.min(left, data.length)
      node.data = data.slice(taken)
      left -= taken
      for (let up = node.parentElement; up && up !== item; up = up.parentElement) cut.add(up)
    },
  })
  // Deepest first: a link that held only the key goes, then the bold around it.
  for (const element of [...cut].reverse()) {
    if (!element.textContent?.trim() && !element.querySelector('img')) element.remove()
  }
}

/** An HTML plan's model, from its sanitised body. `names` are the workspace's projects' keys. */
export function parsePlanHtml(source: string, names: readonly string[], bounds: PlanBounds = PLAN_BOUNDS): PlanModel {
  const fragment = sanitizePlanFragment(source)
  const empty: PlanModel = { objective: null, phasesTitle: '', phases: [], decisions: [], annex: null, diagnostics: [], notes: [], fallback: null }
  const fallBack = (diagnostics: string[]): PlanModel => ({ ...empty, diagnostics, fallback: [...fragment.childNodes] })

  const past = pastBound(fragment, bounds)
  if (past) return fallBack([past])

  const reader = new PlanReader(names)
  const model: PlanModel = { ...empty, notes: reader.notes }
  const diagnostics: string[] = []
  let last = -1
  let phases = false
  for (const child of fragment.childNodes) {
    if (child.nodeType === TEXT_NODE) {
      if ((child as Text).data.trim()) diagnostics.push(`Text stands outside the plan’s sections: “${excerptOf((child as Text).data)}”.`)
      continue
    }
    if (child.nodeType !== ELEMENT_NODE) continue
    const element = child as Element
    const at = TOP.findIndex(([, tag, name]) => isA(element, tag, name))
    if (at < 0) {
      diagnostics.push(`${selector(element)} stands outside the plan’s sections.`)
      continue
    }
    phases ||= TOP[at]![0] === 'phases'
    if (at <= last) {
      diagnostics.push(at === last ? `The plan has a second ${TOP[at]![1]}.${TOP[at]![2]}.` : `${TOP[at]![1]}.${TOP[at]![2]} comes after ${TOP[last]![1]}.${TOP[last]![2]}.`)
      continue
    }
    last = at
    const kind = TOP[at]![0]
    if (kind === 'objective') model.objective = contentOf(element)
    else if (kind === 'phases') {
      const read = reader.phasesSection(element)
      model.phasesTitle = read.title
      model.phases = read.phases
    }
    else if (kind === 'decisions') model.decisions = reader.decisions(element)
    else model.annex = reader.annex(element)
  }
  if (!phases) diagnostics.push('The plan has no section.pp-phases.')
  return diagnostics.length ? fallBack(diagnostics) : model
}
